import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clinicLogo, useClinicBranding } from '../context/ClinicBrandingContext';
import { getAppointments, getClinicInfo, getAdminOrders, getPizasUiConfig, resolveMediaUrl } from '../lib/api';
import {
  isAdminRole,
  isCocina,
  isStaffRole,
  isSuperAdmin,
  isTendero,
  userRoleLabel,
} from '../lib/adminRoles';
import { toast } from 'sonner';
import { playAdminPendingAppointmentAlert } from '../lib/adminPendingAlertSound';
import { Button } from '../components/ui/button';
import LiveClock from '../components/LiveClock';
import SubscriptionPaymentBanner from '../components/SubscriptionPaymentBanner';
import AdminSubscriptionBlocked from '../components/AdminSubscriptionBlocked';
import AdminUnlicensed from '../components/AdminUnlicensed';
import { adminModuleTitle } from '../lib/adminModuleTitle';
import { todayMexicoYmd } from '../lib/dateMexico';
import {
  Package,
  Calendar,
  ClipboardList,
  MessageSquare,
  Settings,
  SlidersHorizontal,
  LogOut,
  Menu,
  X,
  Home,
  LayoutDashboard,
  MessageCircle,
  Globe,
  Users,
  Truck,
  ChefHat,
  Warehouse,
  Tag,
  UtensilsCrossed,
  Banknote,
} from 'lucide-react';

const PENDING_POLL_MS = 12000;
const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';
const DEFAULT_ADMIN_LOGO = "https://customer-assets.emergentagent.com/job_b4b171bf-a957-41aa-a103-fc3ac15c898d/artifacts/jwcnyg2y_logo_Podologo.png";
const PIZAS_BRAND_TITLE = 'Pizzas';

function userDisplayName(u) {
  if (!u) return '';
  const name = String(u.name || '').trim();
  if (name) return name;
  const username = String(u.username || '').trim();
  if (username) return username;
  return String(u.email || '').trim();
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { info: brandingInfo } = useClinicBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);
  const [sidebarMode, setSidebarMode] = useState('auto');
  const [brandLogo, setBrandLogo] = useState(DEFAULT_ADMIN_LOGO);
  const knownPendingIdsRef = useRef(null);
  const [pendingAppointmentCount, setPendingAppointmentCount] = useState(0);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const brandName = String(brandingInfo?.name || '').trim() || PIZAS_BRAND_TITLE;

  useEffect(() => {
    if (loading || !user) return;
    if (!PIZAS_MODE || !isTendero(user.role)) return;
    const allowed =
      location.pathname === '/admin/pedidos' ||
      location.pathname.startsWith('/admin/pedidos/') ||
      location.pathname === '/admin/comanda' ||
      location.pathname.startsWith('/admin/comanda/') ||
      location.pathname === '/admin/caja' ||
      location.pathname.startsWith('/admin/caja/');
    if (!allowed) {
      navigate('/admin/caja', { replace: true });
    }
  }, [loading, user, location.pathname, navigate]);

  useEffect(() => {
    if (!PIZAS_MODE || loading || !user || !isStaffRole(user.role)) return;
    const applyMode = (modeRaw) => {
      const m = String(modeRaw || 'auto').toLowerCase() === 'fixed' ? 'fixed' : 'auto';
      setSidebarMode(m);
      setSidebarHover(m === 'fixed');
    };
    getPizasUiConfig()
      .then((d) => applyMode(d?.admin_sidebar_mode))
      .catch(() => {});
    const onConfigChanged = (e) => {
      if (e?.detail?.admin_sidebar_mode) applyMode(e.detail.admin_sidebar_mode);
    };
    window.addEventListener('pizas-ui-config-changed', onConfigChanged);
    return () => window.removeEventListener('pizas-ui-config-changed', onConfigChanged);
  }, [loading, user]);

  useEffect(() => {
    if (brandingInfo) {
      const resolved = clinicLogo(brandingInfo);
      if (resolved && String(resolved).trim()) {
        setBrandLogo(String(resolved).trim());
        return;
      }
    }
    getClinicInfo()
      .then((d) => {
        if (d?.logo_url && String(d.logo_url).trim()) {
          setBrandLogo(String(d.logo_url).trim());
        } else if (!PIZAS_MODE) {
          setBrandLogo(DEFAULT_ADMIN_LOGO);
        } else {
          setBrandLogo('');
        }
      })
      .catch(() => {});
  }, [brandingInfo]);

  useEffect(() => {
    if (loading || !user || !isStaffRole(user.role)) return undefined;

    const poll = async () => {
      try {
        if (PIZAS_MODE) {
          const list = await getAdminOrders('pending_review', todayMexicoYmd());
          const arr = Array.isArray(list) ? list : [];
          setPendingOrderCount(arr.length);
          return;
        }
        const list = await getAppointments('pending');
        const arr = Array.isArray(list) ? list : [];
        setPendingAppointmentCount(arr.length);
        const ids = new Set(arr.map((a) => a.id).filter(Boolean));
        if (knownPendingIdsRef.current === null) {
          knownPendingIdsRef.current = ids;
          return;
        }
        const prev = knownPendingIdsRef.current;
        const newcomerCount = [...ids].filter((id) => !prev.has(id)).length;
        if (newcomerCount > 0) {
          toast.info(
            newcomerCount === 1
              ? 'Nueva cita pendiente de confirmación'
              : `${newcomerCount} nuevas citas pendientes de confirmación`,
            {
              description:
                'Revisa Citas para confirmarlas por WhatsApp o cancelarlas. El paciente recibirá el aviso cuando confirmes.',
              duration: 10000,
              action: {
                label: 'Ver citas',
                onClick: () => navigate('/admin/citas/por-confirmar'),
              },
            }
          );
          playAdminPendingAppointmentAlert();
        }
        knownPendingIdsRef.current = ids;
      } catch {
        /* red intermitente: no inundar la UI */
      }
    };

    poll();
    const t = window.setInterval(poll, PENDING_POLL_MS);
    return () => window.clearInterval(t);
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Cargando…</div>
      </div>
    );
  }

  if (!user || !isStaffRole(user.role)) {
    return <Navigate to="/admin/login" replace />;
  }

  if (user.admin_licensed === false) {
    return <AdminUnlicensed />;
  }

  if (user.subscription?.access_blocked) {
    return <AdminSubscriptionBlocked />;
  }

  const pizasSuperNav = isSuperAdmin(user.role)
    ? [
        { name: 'Sitio Público', path: '/admin/sitio-publico', icon: Globe, badge: null },
        { name: 'Personal', path: '/admin/administradores', icon: Users, badge: null },
      ]
    : [];

  const navItems = PIZAS_MODE && isCocina(user.role)
    ? [{ name: 'Cocina', path: '/admin/cocina', icon: ChefHat, badge: null }]
    : PIZAS_MODE && isTendero(user.role)
    ? [
        {
          name: 'Comanda',
          path: '/admin/comanda',
          icon: UtensilsCrossed,
          badge: null,
        },
        {
          name: 'Caja',
          path: '/admin/caja',
          icon: Banknote,
          badge: 'pendingOrderCount',
        },
        {
          name: 'Pedidos',
          path: '/admin/pedidos',
          icon: ClipboardList,
          badge: 'pendingOrderCount',
        },
      ]
    : PIZAS_MODE
    ? [
        { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, badge: null },
        {
          name: 'Pedidos',
          path: '/admin/pedidos',
          icon: ClipboardList,
          badge: 'pendingOrderCount',
        },
        { name: 'Comanda', path: '/admin/comanda', icon: UtensilsCrossed, badge: null },
        { name: 'Caja', path: '/admin/caja', icon: Banknote, badge: 'pendingOrderCount' },
        { name: 'Cocina', path: '/admin/cocina', icon: ChefHat, badge: null },
        { name: 'Repartidores', path: '/admin/repartidores', icon: Truck, badge: null },
        { name: 'Menú', path: '/admin/menu', icon: Package, badge: null },
        ...(isAdminRole(user.role)
          ? [{ name: 'Almacén', path: '/admin/almacen', icon: Warehouse, badge: null }]
          : []),
        { name: 'Horarios', path: '/admin/horarios', icon: Calendar, badge: null },
        { name: 'Config tienda', path: '/admin/config-tienda', icon: Settings, badge: null },
        ...(isAdminRole(user.role)
          ? [{ name: 'Cupones', path: '/admin/cupones', icon: Tag, badge: null }]
          : []),
        { name: 'WhatsApp', path: '/admin/whatsapp', icon: MessageCircle, badge: null },
        { name: 'Ajustes', path: '/admin/settings', icon: SlidersHorizontal, badge: null },
        ...pizasSuperNav,
      ]
    : [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, badge: null },
    { name: 'Productos', path: '/admin/productos', icon: Package, badge: null },
    { name: 'Citas', path: '/admin/citas', icon: Calendar, badge: null },
    {
      name: 'Citas por confirmar',
      path: '/admin/citas/por-confirmar',
      icon: ClipboardList,
      badge: 'pendingCount',
    },
    { name: 'Reseñas', path: '/admin/resenas', icon: MessageSquare, badge: null },
    { name: 'Horarios', path: '/admin/horarios', icon: Settings, badge: null },
    { name: 'WhatsApp', path: '/admin/whatsapp', icon: MessageCircle, badge: null },
    { name: 'Ajustes', path: '/admin/settings', icon: SlidersHorizontal, badge: null },
    ...(isSuperAdmin(user.role)
      ? [
          { name: 'Sitio Público', path: '/admin/sitio-publico', icon: Globe, badge: null },
          { name: 'Personal', path: '/admin/administradores', icon: Users, badge: null },
        ]
      : []),
  ];

  const isActive = (path) => {
    const p = location.pathname;
    if (path === '/admin/cocina') return p === path || p.startsWith(`${path}/`);
    if (path === '/admin/pedidos') return p === path || p.startsWith(`${path}/`);
    if (path === '/admin/repartidores') return p === path || p.startsWith(`${path}/`);
    if (path === '/admin/sitio-publico') return p === path || p.startsWith(`${path}/`);
    if (path === '/admin/citas/por-confirmar') return p === path || p.startsWith(`${path}/`);
    if (path === '/admin/citas') return p.startsWith(path) && !p.startsWith('/admin/citas/por-confirmar');
    if (path === '/admin') return p === '/admin';
    return p.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const loggedInLabel = userDisplayName(user);
  const loggedInRoleLabel = userRoleLabel(user?.role);
  const sidebarFixed = PIZAS_MODE && sidebarMode === 'fixed';
  const showSidebarDesktop = sidebarFixed || sidebarHover;
  const showSidebar = sidebarOpen || showSidebarDesktop;
  const moduleTitle = adminModuleTitle(location.pathname);

  const closeMobileSidebar = () => setSidebarOpen(false);
  const closeDesktopSidebar = () => {
    if (sidebarFixed) return;
    setSidebarHover(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-layout">
      {/* Zona sensible en el borde izquierdo (escritorio): solo en modo automático */}
      {!sidebarFixed ? (
        <div
          className="hidden lg:block fixed left-0 top-0 z-[45] h-full w-3"
          aria-hidden
          onMouseEnter={() => setSidebarHover(true)}
          data-testid="admin-sidebar-edge-hover"
        />
      ) : null}

      {/* Barra superior móvil */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border px-3 py-2 flex items-center gap-2">
        <button
          type="button"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          onClick={() => setSidebarOpen((o) => !o)}
          aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <h1 className="flex-1 min-w-0 font-heading text-lg font-bold text-foreground truncate">
          {moduleTitle || brandName}
        </h1>
        {PIZAS_MODE ? <LiveClock compact className="scale-90 origin-right" /> : null}
      </div>

      {/* Barra superior escritorio (no tapa el menú lateral; se desplaza cuando el panel abre) */}
      <div
        className={`hidden lg:flex fixed top-0 right-0 z-30 h-14 items-center gap-3 border-b border-border bg-white shadow-sm px-4 transition-[left] duration-200 ease-out ${
          showSidebarDesktop ? 'left-64' : 'left-0'
        }`}
      >
        <h1 className="min-w-0 flex-1 font-heading text-xl font-bold text-foreground truncate">
          {moduleTitle || (PIZAS_MODE ? brandName : 'Panel de administración')}
        </h1>
        {PIZAS_MODE ? <LiveClock compact /> : null}
      </div>

      {/* Panel lateral: hamburguesa + logo se mueven juntos */}
      <aside
        className={`fixed top-0 left-0 z-40 hidden lg:flex h-screen w-64 flex-col border-r border-border bg-white shadow-lg transition-transform duration-200 ease-out ${
          showSidebarDesktop ? 'translate-x-0' : '-translate-x-full'
        }`}
        onMouseEnter={() => {
          if (!sidebarFixed) setSidebarHover(true);
        }}
        onMouseLeave={closeDesktopSidebar}
        data-testid="admin-sidebar"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 flex h-14 items-center gap-2 border-b border-border px-3 bg-white">
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-foreground hover:bg-muted transition-colors"
              onClick={() => {
                if (sidebarFixed) return;
                if (showSidebarDesktop) closeDesktopSidebar();
                else setSidebarHover(true);
              }}
              aria-label={
                sidebarFixed
                  ? 'Menú lateral fijo'
                  : showSidebarDesktop
                    ? 'Cerrar menú lateral'
                    : 'Abrir menú lateral'
              }
              aria-expanded={showSidebarDesktop}
              disabled={sidebarFixed}
              data-testid="desktop-sidebar-toggle"
            >
              {sidebarFixed ? (
                <Menu className="w-5 h-5 opacity-50" />
              ) : showSidebarDesktop ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
            <div className="min-w-0 flex-1">
              {PIZAS_MODE && brandLogo ? (
                <img
                  src={resolveMediaUrl(brandLogo)}
                  alt="Logo"
                  className="h-9 max-w-[140px] object-contain object-left"
                />
              ) : PIZAS_MODE ? (
                <p className="font-heading text-base font-bold text-primary truncate">{brandName}</p>
              ) : (
                <img src={brandLogo} alt="Logo" className="h-9 max-w-[140px] object-contain object-left" />
              )}
            </div>
          </div>

          <div className="shrink-0 border-b border-border px-4 py-3">
            {loggedInLabel ? (
              <p
                className="text-sm font-semibold text-foreground truncate"
                title={`${loggedInLabel} · ${loggedInRoleLabel}`}
              >
                {loggedInLabel}
              </p>
            ) : null}
            <p className="text-xs font-medium text-primary mt-0.5 truncate" title={loggedInRoleLabel}>
              {loggedInRoleLabel}
            </p>
            {PIZAS_MODE ? (
              <p className="text-[11px] text-muted-foreground mt-0.5">Pedidos y menú</p>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-0.5">Panel de administración</p>
            )}
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 space-y-0.5">
            {navItems.map((item) => {
              const count =
                item.badge === 'pendingCount'
                  ? pendingAppointmentCount
                  : item.badge === 'pendingOrderCount'
                    ? pendingOrderCount
                    : 0;
              const badgeLabel =
                count > 99 ? '99+' : count > 0 ? String(count) : null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    closeMobileSidebar();
                    closeDesktopSidebar();
                  }}
                  className={`group relative flex min-h-[44px] items-center gap-3 rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground font-semibold shadow-md shadow-primary/25'
                      : 'text-foreground/75 hover:bg-primary/10 hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="relative shrink-0 inline-flex items-center justify-center w-9 h-9">
                    <item.icon className="w-5 h-5" />
                    {badgeLabel ? (
                      <span
                        className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] leading-[18px] font-bold text-center shadow-sm border ${
                          isActive(item.path)
                            ? 'bg-red-600 text-white border-white/90'
                            : 'bg-[#EA4335] text-white border-white'
                        }`}
                        aria-label={`${count} citas por confirmar`}
                      >
                        {badgeLabel}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex-1 truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="shrink-0 border-t border-border p-3 space-y-1">
            <Link
              to="/"
              onClick={() => {
                closeMobileSidebar();
                closeDesktopSidebar();
              }}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground/75 hover:bg-primary/10 hover:text-foreground transition-colors"
              data-testid="nav-home"
            >
              <Home className="w-5 h-5" />
              Ver Sitio
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-3 h-9 text-accent hover:text-accent hover:bg-accent/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Menú móvil (panel completo) */}
      <aside
        className={`fixed top-0 left-0 z-40 flex lg:hidden h-screen w-64 flex-col border-r border-border bg-white shadow-lg transition-transform duration-200 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar-mobile"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-border p-4 pt-14">
            {PIZAS_MODE && brandLogo ? (
              <img src={resolveMediaUrl(brandLogo)} alt="Logo" className="h-10 max-w-[180px] object-contain" />
            ) : PIZAS_MODE ? (
              <div className="flex items-center gap-2">
                <span className="text-2xl">🍕</span>
                <div>
                  <p className="font-heading text-lg font-bold text-primary">{brandName}</p>
                  <p className="text-xs text-muted-foreground">Panel pizzería</p>
                </div>
              </div>
            ) : (
              <img src={brandLogo} alt="Logo" className="h-10 max-w-[180px] object-contain" />
            )}
            {loggedInLabel ? (
              <p
                className="text-sm font-semibold text-foreground mt-2 truncate"
                title={`${loggedInLabel} · ${loggedInRoleLabel}`}
              >
                {loggedInLabel}
              </p>
            ) : null}
            <p className="text-xs font-medium text-primary mt-0.5 truncate" title={loggedInRoleLabel}>
              {loggedInRoleLabel}
            </p>
            {PIZAS_MODE ? (
              <p className="text-[11px] text-muted-foreground mt-0.5">Pedidos y menú</p>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-0.5">Panel de administración</p>
            )}
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 space-y-0.5">
            {navItems.map((item) => {
              const count =
                item.badge === 'pendingCount'
                  ? pendingAppointmentCount
                  : item.badge === 'pendingOrderCount'
                    ? pendingOrderCount
                    : 0;
              const badgeLabel =
                count > 99 ? '99+' : count > 0 ? String(count) : null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileSidebar}
                  className={`group relative flex min-h-[44px] items-center gap-3 rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground font-semibold shadow-md shadow-primary/25'
                      : 'text-foreground/75 hover:bg-primary/10 hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="relative shrink-0 inline-flex items-center justify-center w-9 h-9">
                    <item.icon className="w-5 h-5" />
                    {badgeLabel ? (
                      <span
                        className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] leading-[18px] font-bold text-center shadow-sm border ${
                          isActive(item.path)
                            ? 'bg-red-600 text-white border-white/90'
                            : 'bg-[#EA4335] text-white border-white'
                        }`}
                        aria-label={`${count} citas por confirmar`}
                      >
                        {badgeLabel}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex-1 truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="shrink-0 border-t border-border p-3 space-y-1">
            <Link
              to="/"
              onClick={closeMobileSidebar}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground/75 hover:bg-primary/10 hover:text-foreground transition-colors"
              data-testid="nav-home-mobile"
            >
              <Home className="w-5 h-5" />
              Ver Sitio
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-3 h-9 text-accent hover:text-accent hover:bg-accent/10"
              onClick={handleLogout}
              data-testid="logout-btn-mobile"
            >
              <LogOut className="w-5 h-5" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={closeMobileSidebar}
          aria-hidden
        />
      )}

      {/* Clic fuera del menú (escritorio) también lo cierra — solo modo automático */}
      {sidebarHover && !sidebarFixed && (
        <div
          className="hidden lg:block fixed inset-0 z-[35] bg-black/10"
          onClick={closeDesktopSidebar}
          aria-hidden
        />
      )}

      {/* Contenido: ancho completo (menú oculto) o desplazado a la derecha (menú fijo en escritorio) */}
      <main
        className={`min-h-screen pt-[4.5rem] lg:pt-14 transition-[margin-left] duration-200 ease-out ${
          sidebarFixed ? 'lg:ml-64' : 'w-full'
        }`}
        data-testid="admin-main-content"
      >
        <div className="mx-auto w-full max-w-[1600px] p-4 sm:p-6 lg:p-8">
          <SubscriptionPaymentBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
